// Odia Number Conversion Utilities

// English to Odia digit mapping
const ENGLISH_TO_ODIA_DIGITS = {
  '0': '୦',
  '1': '୧', 
  '2': '୨',
  '3': '୩',
  '4': '୪',
  '5': '୫',
  '6': '୬',
  '7': '୭',
  '8': '୮',
  '9': '୯'
};

// Odia to English digit mapping (for reverse conversion)
const ODIA_TO_ENGLISH_DIGITS = {
  '୦': '0',
  '୧': '1',
  '୨': '2', 
  '୩': '3',
  '୪': '4',
  '୫': '5',
  '୬': '6',
  '୭': '7',
  '୮': '8',
  '୯': '9'
};

/**
 * Convert English digits to Odia digits
 * @param {string|number} input - Number or string containing numbers
 * @returns {string} - String with Odia digits
 */
export const convertToOdiaDigits = (input) => {
  if (input === null || input === undefined) return '';
  
  const str = String(input);
  return str.replace(/[0-9]/g, (digit) => ENGLISH_TO_ODIA_DIGITS[digit] || digit);
};

/**
 * Convert Odia digits to English digits
 * @param {string} input - String containing Odia digits
 * @returns {string} - String with English digits
 */
export const convertToEnglishDigits = (input) => {
  if (!input) return '';
  
  const str = String(input);
  return str.replace(/[୦-୯]/g, (digit) => ODIA_TO_ENGLISH_DIGITS[digit] || digit);
};

/**
 * Format number with Odia digits and Indian number system
 * @param {number} num - Number to format
 * @param {object} options - Formatting options
 * @returns {string} - Formatted number in Odia
 */
export const formatOdiaNumber = (num, options = {}) => {
  if (num === null || num === undefined || isNaN(num)) return '';
  
  const {
    useIndianFormat = true,
    decimals = 2,
    showDecimals = false
  } = options;
  
  let formattedNum;
  
  if (useIndianFormat) {
    // Indian number format (lakhs, crores)
    formattedNum = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: showDecimals ? decimals : 0,
      minimumFractionDigits: showDecimals ? decimals : 0
    }).format(num);
  } else {
    // Standard format
    formattedNum = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: showDecimals ? decimals : 0,
      minimumFractionDigits: showDecimals ? decimals : 0
    }).format(num);
  }
  
  return convertToOdiaDigits(formattedNum);
};

/**
 * Convert numbers in a string to Odia while preserving other text
 * @param {string} text - Text containing numbers
 * @returns {string} - Text with Odia digits
 */
export const convertNumbersInText = (text) => {
  if (!text) return '';
  
  return String(text).replace(/\d+(\.\d+)?/g, (match) => {
    return convertToOdiaDigits(match);
  });
};

/**
 * Check if current language is Odia
 * @returns {boolean} - True if Odia language is selected
 */
export const isOdiaLanguage = () => {
  const currentLang = localStorage.getItem('lang') || 'en';
  return currentLang === 'ori' || currentLang === 'or';
};

/**
 * Smart number converter - converts only if Odia language is active
 * @param {string|number} input - Input to convert
 * @param {object} options - Formatting options
 * @returns {string} - Converted or original input based on language
 */
export const smartConvertNumber = (input, options = {}) => {
  if (!isOdiaLanguage()) {
    return String(input);
  }
  
  if (typeof input === 'number') {
    console.log("convert number", input)
    return formatOdiaNumber(input, options);
  }
  
  return convertNumbersInText(input);
};


/**
 * Convert percentage to Odia
 * @param {number} percentage - Percentage value
 * @returns {string} - Formatted percentage in Odia
 */
export const convertOdiaPercentage = (percentage) => {
  if (!isOdiaLanguage()) return `${percentage}%`;
  
  return `${convertToOdiaDigits(percentage)}%`;
};

/**
 * Convert currency to Odia format
 * @param {number} amount - Currency amount
 * @param {string} currency - Currency symbol (₹, $, etc.)
 * @returns {string} - Formatted currency in Odia
 */
export const convertOdiaCurrency = (amount, currency = '₹') => {
  if (!isOdiaLanguage()) return `${currency}${amount}`;
  
  const formattedAmount = formatOdiaNumber(amount, { useIndianFormat: true });
  return `${currency}${formattedAmount}`;
};